import React from 'react';
import { getRelativeTime } from 'utils/date-from-now';
import { buildClassName } from 'utils/build-class-name';
import styles from './style.module.scss';

import { PropsWithTestId } from 'types';
import { StandardStatus } from '@zoocasa/go-search';
import { LOCALE_COMMON_PATHS, Namespaces } from 'types/locales';
import { useTranslation } from 'next-i18next';
import { isExpired, isActive, isSold, isPending } from 'types/reso';

type TimestampProps = PropsWithTestId<{
  timestamp: Date | null;
  status: StandardStatus;
  isRental: boolean;
  className?: string;
}>;

export default function Timestamp({ timestamp, status, className, isRental, testId }: TimestampProps) {
  const { t } = useTranslation(Namespaces.COMMON);
  
  // Don't render if timestamp is null (e.g., Quebec listings)
  if (!timestamp) {
    return <></>;
  }
  
  const isFutureDate = new Date() < new Date(timestamp);
  let labelContext = '';

  if (isFutureDate) {
    return <></>;
  }

  if (isActive(status)) {
    labelContext = 'active';
  } else if (isSold(status)) {
    labelContext = isRental ? 'rented' : 'sold';
  } else if (isExpired(status)) {
    labelContext = 'expired';
  } else if (isPending(status)) {
    labelContext = 'pending';
  }

  const [value, unit] = getRelativeTime(timestamp);
  const dateLabel = t(LOCALE_COMMON_PATHS.RELATIVE_TIME, { val: value, range: unit, numeric: 'auto' });
  const shortDateLabel = t(LOCALE_COMMON_PATHS.RELATIVE_TIME, { val: value, range: unit, numeric: 'auto', style: 'narrow' });

  const classNames = buildClassName(styles.container, className);
  return (
    <div className={classNames}>
      <p
        className={styles.component}
        data-testid={testId}
        data-full-text={
          t(LOCALE_COMMON_PATHS.LISTING_CARD_TIMESTAMP_LABEL,
            {
              date: dateLabel,
              context: labelContext }
          )}
        data-small-text={
          t(LOCALE_COMMON_PATHS.LISTING_CARD_TIMESTAMP_LABEL,
            {
              date: shortDateLabel,
              context: labelContext }
          )}
      />
    </div>
  );
}
